import { useEffect, useState } from "react";
import { apiError } from "@/lib/api";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Phone, MessageCircle, Mail, Plus, ChevronRight, ChevronLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Kanban({ title, subtitle, stages, fetchList, createItem, moveStage, testid }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", phone: "", whatsapp: "", email: "", source: "", notes: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const load = async () => { setLoading(true); try { setRows(await fetchList()); } catch { setRows([]); } setLoading(false); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const add = async () => {
    try { await createItem({ ...f, stage: stages[0] }); toast.success("Prospect ajouté"); setOpen(false); setF({ name: "", phone: "", whatsapp: "", email: "", source: "", notes: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const move = async (row, dir) => {
    const idx = stages.indexOf(row.stage);
    const ni = Math.min(stages.length - 1, Math.max(0, idx + dir));
    if (ni === idx) return;
    setRows((r) => r.map((x) => (x.id === row.id ? { ...x, stage: stages[ni] } : x)));
    try { await moveStage(row.id, stages[ni]); } catch (e) { toast.error(apiError(e)); load(); }
  };

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle}>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="vp-accent-bg text-black font-medium" data-testid={`${testid}-add-btn`}><Plus size={16} className="mr-1" /> Nouveau prospect</Button></DialogTrigger>
          <DialogContent className="vp-card border-white/10 text-white max-w-md">
            <DialogHeader><DialogTitle className="font-display text-2xl">Nouveau prospect</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label className="text-slate-300 text-sm mb-1.5 block">Nom</Label><Input value={f.name} onChange={set("name")} className="bg-black/30 border-white/10 text-white" data-testid={`${testid}-name`} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-slate-300 text-sm mb-1.5 block">Téléphone</Label><Input value={f.phone} onChange={set("phone")} className="bg-black/30 border-white/10 text-white" /></div>
                <div><Label className="text-slate-300 text-sm mb-1.5 block">WhatsApp</Label><Input value={f.whatsapp} onChange={set("whatsapp")} className="bg-black/30 border-white/10 text-white" /></div>
              </div>
              <div><Label className="text-slate-300 text-sm mb-1.5 block">Email</Label><Input value={f.email} onChange={set("email")} className="bg-black/30 border-white/10 text-white" /></div>
              <div><Label className="text-slate-300 text-sm mb-1.5 block">Source</Label><Input value={f.source} onChange={set("source")} className="bg-black/30 border-white/10 text-white" placeholder="Instagram, Site, Recommandation…" /></div>
              <div><Label className="text-slate-300 text-sm mb-1.5 block">Notes</Label><Textarea value={f.notes} onChange={set("notes")} className="bg-black/30 border-white/10 text-white" /></div>
            </div>
            <DialogFooter><Button onClick={add} className="vp-accent-bg text-black font-medium" data-testid={`${testid}-save-btn`}>Ajouter</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {loading ? <div className="text-slate-400"><Loader2 className="animate-spin inline mr-2" size={16} /> Chargement…</div> : (
        <div className="flex gap-4 overflow-x-auto pb-4 no-scrollbar">
          {stages.map((stage) => {
            const items = rows.filter((r) => r.stage === stage);
            return (
              <div key={stage} className="w-72 shrink-0">
                <div className="flex items-center justify-between mb-3 px-1">
                  <span className="text-sm font-semibold text-white">{stage}</span>
                  <span className="text-xs text-slate-500 mono">{items.length}</span>
                </div>
                <div className="space-y-3 min-h-[100px]">
                  {items.map((row) => (
                    <div key={row.id} className="vp-card rounded-lg p-4 hover-lift" data-testid="crm-prospect-card">
                      <div className="font-medium text-white text-sm">{row.name}</div>
                      {row.boutique && <div className="text-xs text-slate-500">{row.boutique}</div>}
                      {row.source && <div className="text-xs vp-accent-text mt-0.5">{row.source}</div>}
                      {row.notes && <p className="text-xs text-slate-400 mt-2 line-clamp-2">{row.notes}</p>}
                      <div className="flex items-center gap-3 mt-3 text-slate-500">
                        {row.phone && <a href={`tel:${row.phone}`} className="hover:text-white"><Phone size={14} /></a>}
                        {row.whatsapp && <a href={`https://wa.me/${row.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="hover:text-emerald-400"><MessageCircle size={14} /></a>}
                        {row.email && <a href={`mailto:${row.email}`} className="hover:text-white"><Mail size={14} /></a>}
                        <div className="ml-auto flex gap-1">
                          <button onClick={() => move(row, -1)} className="hover:text-white disabled:opacity-30" disabled={stages.indexOf(row.stage) === 0}><ChevronLeft size={16} /></button>
                          <button onClick={() => move(row, 1)} className="hover:text-white disabled:opacity-30" disabled={stages.indexOf(row.stage) === stages.length - 1} data-testid="crm-move-next"><ChevronRight size={16} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
